import type { ArbetsomradeSummary, Bildspel, Block, Lararhandledning, Lektion, LektionIndexPost, Prov } from '../types'
import arbetsomradenJson from '../content/lektionsplanering/arbetsomraden.json'
import lektionsindexJson from '../content/lektionsplanering/lektionsindex.json'
import blockbibliotekJson from '../content/lektionsplanering/blockbibliotek.json'

export const arbetsomraden = arbetsomradenJson as ArbetsomradeSummary[]
export const lektionsindex = lektionsindexJson as LektionIndexPost[]
export const blockbibliotek = blockbibliotekJson as Block[]

const lararhandledningModuler = import.meta.glob<{ default: Lararhandledning }>(
  '../content/lektionsplanering/lararhandledning/*.json',
)
const lektionModuler = import.meta.glob<{ default: Lektion }>('../content/lektionsplanering/lektioner/*.json')
const provModuler = import.meta.glob<{ default: Prov }>('../content/lektionsplanering/prov/*.json')
const bildspelModuler = import.meta.glob<{ default: Bildspel }>('../content/lektionsplanering/bildspel/*.json')

export function hittaArbetsomrade(id: string): ArbetsomradeSummary | null {
  return arbetsomraden.find((a) => a.id === id) ?? null
}

export async function laddaLararhandledning(arbetsomradeId: string): Promise<Lararhandledning | null> {
  const laddare = lararhandledningModuler[`../content/lektionsplanering/lararhandledning/${arbetsomradeId}.json`]
  if (!laddare) return null
  return (await laddare()).default
}

export async function laddaLektion(lektionId: string): Promise<Lektion | null> {
  const laddare = lektionModuler[`../content/lektionsplanering/lektioner/${lektionId}.json`]
  if (!laddare) return null
  return (await laddare()).default
}

export async function laddaProv(arbetsomradeId: string): Promise<Prov | null> {
  const laddare = provModuler[`../content/lektionsplanering/prov/${arbetsomradeId}.json`]
  if (!laddare) return null
  return (await laddare()).default
}

export async function laddaBildspel(arbetsomradeId: string): Promise<Bildspel | null> {
  const laddare = bildspelModuler[`../content/lektionsplanering/bildspel/${arbetsomradeId}.json`]
  if (!laddare) return null
  return (await laddare()).default
}

/** Slår upp startsidan i bildspelet för en lektions slide_deck_avsnitt
 *  (t.ex. "lektionsplanering/slides/ao2.md#motivation-grunderna" → sidindex). */
export function bildspelStartsida(bildspel: Bildspel, slideDeckAvsnitt: string | undefined): number {
  const ankare = slideDeckAvsnitt?.split('#')[1]
  if (!ankare) return 0
  return bildspel.avsnitt.find((a) => a.ankare === ankare)?.startSida ?? 0
}

/** "1 lektion" men "0 lektioner" och "11 lektioner". */
export const lektionsord = (antal: number) => (antal === 1 ? 'lektion' : 'lektioner')

export function lektionerForArbetsomrade(arbetsomradeId: string): LektionIndexPost[] {
  return lektionsindex
    .filter((l) => l.arbetsomrade_id === arbetsomradeId)
    .sort((a, b) => a.lektionsnummer - b.lektionsnummer)
}

export const BLOCK_TYP_ETIKETT: Record<string, string> = {
  uppstart: 'Uppstart',
  genomgang: 'Genomgång',
  aktivitet: 'Aktivitet',
  avslut: 'Avslut',
  flex_fordjupning: 'Fördjupning',
  flex_exit_ticket: 'Exit ticket',
  flex_fardighetstraning: 'Färdighetsträning',
  flex_case: 'Case',
}

export function grannLektion(lektionId: string): { forra: LektionIndexPost | null; nasta: LektionIndexPost | null } {
  const post = lektionsindex.find((l) => l.lektion_id === lektionId)
  if (!post) return { forra: null, nasta: null }
  const iOmrade = lektionerForArbetsomrade(post.arbetsomrade_id)
  const i = iOmrade.findIndex((l) => l.lektion_id === lektionId)
  return {
    forra: i > 0 ? iOmrade[i - 1] : null,
    nasta: i >= 0 && i < iOmrade.length - 1 ? iOmrade[i + 1] : null,
  }
}
